import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { faBook } from '@fortawesome/pro-solid-svg-icons/faBook'
import { RankmiTable } from '@rankmi/rankmi-table'
import { format } from 'date-fns'
import { ICourse } from 'interfaces/learningPath'
import { rolesService } from 'api/configuration/roles'
import { CourseStateBadge } from 'components/reusables/states'
import EmptyState from 'components/reusables/emptyState'
import { useRolesAndPermissions } from '../../../../../context/RolesAndPermissionsContext'
import { Wrapper } from './styled'

interface IProps {
  selectedCourses: ICourse[]
  setSelectedCourses: (value: ICourse[]) => void
}

const Courses = ({ selectedCourses, setSelectedCourses }: IProps) => {
  const { t } = useTranslation()
  const prefix =
    'administration.learningPaths.manage.create.content.collections.courses.addCourse'
  const [courses, setCourses] = useState<ICourse[]>([])
  const [loading, setLoading] = useState(false)
  const [searchCriteria, setSearchCriteria] = useState('')
  const [order, setOrder] = useState('ascend')
  const [fieldToOrder, setFieldToOrder] = useState('name')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)
  const { roleDetail: data } = useRolesAndPermissions()

  useEffect(() => {
    getCourses(currentPage, searchCriteria, order, fieldToOrder)
  }, [])

  const getCourses = async (
    page: number,
    criteria: string,
    order: string,
    fieldToOrder: string
  ) => {
    setLoading(true)
    await rolesService
      .getCourses(page, criteria, order, fieldToOrder)
      .then((response) => {
        const existingCourseIds = data.courses.map((course) => course.id)

        const filteredCourses = response.data.courses
          .filter((course) => !existingCourseIds.includes(course.id))
          .map((course) => ({ ...course, key: course.id }))

        setCourses(filteredCourses)
        setTotalRecords(response.data.total - existingCourseIds.length)
      })
    setLoading(false)
  }

  const onSearch = (value: string) => {
    setSearchCriteria(value)
    setCurrentPage(1)
    getCourses(1, value, order, fieldToOrder)
  }

  const onPaginationChange = (value: number) => {
    setCurrentPage(value)
    getCourses(value, searchCriteria, order, fieldToOrder)
  }

  const onSortChange = (value: { order: string; field: string }) => {
    setOrder(value.order)
    setFieldToOrder(value.field)
    getCourses(currentPage, searchCriteria, value.order, value.field)
  }

  const onRowSelected = (value: any) => {
    if (value['dispatcher'] !== 'handleUnselectAllRowsOnFilter') {
      const courseList: ICourse[] = [
        ...selectedCourses,
        ...value['selectedRows'],
      ]
      const addedCourses: number[] = []
      setSelectedCourses(
        courseList.filter((course) => {
          if (
            value['selectedRowKeys'].includes(course.id) &&
            !addedCourses.includes(course.id)
          ) {
            addedCourses.push(course.id)
            return true
          }
          return false
        })
      )
    }
  }

  const locale = {
    header: {
      rowsCount: t(`${prefix}.rowsCount`),
      rowsCount_plural: t(`${prefix}.rowsCountPlural`),
    },
  }

  const columns = [
    {
      title: t(`${prefix}.columns.name`),
      sorteable: true,
      key: 'name',
      render: (name: string) => name,
    },
    {
      title: t(`${prefix}.columns.createdAt`),
      sorteable: true,
      width: 160,
      key: 'createdAt',
      render: (createdAt: string) =>
        format(new Date(createdAt), 'dd-MM-yyyy kk:mm'),
    },
    {
      title: t(`${prefix}.columns.state`),
      sorteable: true,
      width: 150,
      key: 'state',
      render: (state: number) => <CourseStateBadge state={state} />,
    },
  ]

  return (
    <Wrapper>
      <RankmiTable
        locale={locale}
        columnHeaders={columns}
        data={courses}
        total={totalRecords}
        countIcon={faBook}
        search={searchCriteria}
        onSearch={onSearch}
        onRowSelected={onRowSelected}
        onPaginationChange={onPaginationChange}
        selectedRows={selectedCourses.map((row) => row.id)}
        onSortChange={onSortChange}
        currentPage={currentPage}
        loading={loading}
        delaySearch={true}
        customEmpty={
          <EmptyState title={t(`${prefix}.emptyState`)} icon={faBook} />
        }
      />
    </Wrapper>
  )
}

export default Courses
